// MultiFileUI.tsx - UI Components for Multi-File Upload
import React from 'react';
import { X, RotateCcw, FileText, Image, Video, Loader, Upload } from 'lucide-react';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface FileItem {
  id: string;
  file: File;
  type: string;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error?: string;
  uploadedId?: string;
}

// ============================================================================
// FILE ICON COMPONENT
// ============================================================================

const FileIcon: React.FC<{ type: string; size?: number }> = ({ type, size = 16 }) => {
  const iconProps = { size, style: { flexShrink: 0 } };
  
  switch (type) {
    case 'image':
      return <Image {...iconProps} color="#4CAF50" />;
    case 'video':
      return <Video {...iconProps} color="#2196F3" />;
    case 'document':
    case 'pdf':
    default:
      return <FileText {...iconProps} color="#FF9800" />;
  }
};

// ============================================================================
// SINGLE FILE ITEM COMPONENT
// ============================================================================

interface FileItemProps {
  file: FileItem;
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
}

const FileItemUI: React.FC<FileItemProps> = ({ file, onRemove, onRetry }) => {
  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Status colors
  const getStatusColor = (): string => {
    switch (file.status) {
      case 'pending': return '#FFA726';
      case 'uploading': return '#42A5F5';
      case 'completed': return '#4CAF50';
      case 'failed': return '#EF5350';
      default: return '#9E9E9E';
    }
  };

  // Status text
  const getStatusText = (): string => {
    switch (file.status) {
      case 'pending': return 'Waiting...';
      case 'uploading': return `${file.progress}%`;
      case 'completed': return 'Sent';
      case 'failed': return file.error || 'Failed';
      default: return 'Unknown';
    }
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    background: 'white',
    borderRadius: '6px',
    border: '1px solid #e0e0e0',
    marginBottom: '4px',
    fontSize: '13px',
    gap: '8px'
  };

  const fileInfoStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  };

  const fileNameStyle: React.CSSProperties = {
    fontWeight: '500',
    color: '#333',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '200px'
  };

  const fileMetaStyle: React.CSSProperties = {
    fontSize: '11px',
    color: '#666',
    display: 'flex',
    gap: '8px'
  };

  const statusStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '500',
    color: getStatusColor(),
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
  };

  const progressBarStyle: React.CSSProperties = {
    width: '40px',
    height: '3px',
    background: '#f0f0f0',
    borderRadius: '2px',
    overflow: 'hidden'
  };

  const progressFillStyle: React.CSSProperties = {
    height: '100%',
    background: getStatusColor(),
    width: `${file.progress}%`,
    transition: 'width 0.3s ease'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '4px',
    border: 'none',
    background: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background 0.2s ease'
  };

  return (
    <div style={containerStyle}>
      {/* File Icon */}
      <FileIcon type={file.type} />
      
      {/* File Info */}
      <div style={fileInfoStyle}>
        <div style={fileNameStyle} title={file.file.name}>
          {file.file.name}
        </div>
        <div style={fileMetaStyle}>
          <span>{formatSize(file.file.size)}</span>
          <span>•</span>
          <span>{file.type.toUpperCase()}</span>
        </div>
      </div>
      
      {/* Status */}
      <div style={statusStyle}>
        {file.status === 'uploading' && (
          <div style={{
            width: '10px',
            height: '10px',
            border: '1px solid #e0e0e0',
            borderTop: '1px solid #42A5F5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        )}
        <span>{getStatusText()}</span>
      </div>
      
      {/* Progress Bar */}
      {file.status === 'uploading' && (
        <div style={progressBarStyle}>
          <div style={progressFillStyle} />
        </div>
      )}
      
      {/* Actions */}
      <div style={{ display: 'flex', gap: '2px' }}>
        {/* Retry button */}
        {file.status === 'failed' && onRetry && (
          <button
            style={buttonStyle}
            onClick={() => onRetry(file.id)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            title="Retry upload"
          >
            <RotateCcw size={12} color="#666" />
          </button>
        )}
        
        {/* Remove button */}
        {file.status !== 'uploading' && (
          <button
            style={buttonStyle}
            onClick={() => onRemove(file.id)}
            onMouseEnter={(e) => e.currentTarget.style.background = '#fee'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            title="Remove file"
          >
            <X size={12} color="#666" />
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN FILE QUEUE COMPONENT
// ============================================================================

interface MultiFileQueueProps {
  files: FileItem[];
  uploading: boolean;
  onRemove: (id: string) => void;
  onRetry?: (id: string) => void;
  onClearCompleted?: () => void;
}

export const MultiFileQueue: React.FC<MultiFileQueueProps> = ({
  files,
  uploading,
  onRemove,
  onRetry,
  onClearCompleted
}) => {
  // Don't render if no files
  if (files.length === 0) return null;

  const completedCount = files.filter(f => f.status === 'completed').length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const pendingCount = files.filter(f => f.status === 'pending').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;

  const containerStyle: React.CSSProperties = {
    background: '#f8f9fa',
    borderRadius: '8px',
    padding: '12px',
    margin: '8px 0',
    border: '1px solid #e9ecef'
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#333'
  };

  const titleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };

  const statsStyle: React.CSSProperties = {
    fontSize: '11px',
    color: '#666',
    display: 'flex',
    gap: '12px',
    marginBottom: '8px'
  };

  const clearButtonStyle: React.CSSProperties = {
    padding: '4px 8px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    background: '#e3f2fd',
    color: '#1976d2',
    cursor: 'pointer',
    transition: 'background 0.2s ease'
  };

  const fileListStyle: React.CSSProperties = {
    maxHeight: '200px',
    overflowY: 'auto'
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={titleStyle}>
          <Upload size={14} color="#666" />
          <span>Upload Queue ({files.length})</span>
          {uploading && (
            <div style={{
              width: '12px',
              height: '12px',
              border: '1px solid #e0e0e0',
              borderTop: '1px solid #2196F3',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </div>
        
        {completedCount > 0 && onClearCompleted && (
          <button
            style={clearButtonStyle}
            onClick={onClearCompleted}
            onMouseEnter={(e) => e.currentTarget.style.background = '#bbdefb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#e3f2fd'}
          >
            Clear ({completedCount})
          </button>
        )}
      </div>
      
      {/* Stats */}
      <div style={statsStyle}>
        {pendingCount > 0 && <span>🟡 {pendingCount} pending</span>}
        {uploadingCount > 0 && <span>🔵 {uploadingCount} uploading</span>}
        {completedCount > 0 && <span>🟢 {completedCount} sent</span>}
        {failedCount > 0 && <span>🔴 {failedCount} failed</span>}
      </div>
      
      {/* File List */}
      <div style={fileListStyle}>
        {files.map(file => (
          <FileItemUI
            key={file.id}
            file={file}
            onRemove={onRemove}
            onRetry={onRetry}
          />
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// DRAG OVERLAY COMPONENT
// ============================================================================

interface DragOverlayProps {
  isDragActive: boolean;
}

export const DragOverlay: React.FC<DragOverlayProps> = ({ isDragActive }) => {
  if (!isDragActive) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 132, 255, 0.1)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    pointerEvents: 'none'
  };

  const contentStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)',
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    border: '2px dashed rgba(0, 132, 255, 0.6)'
  };

  const iconStyle: React.CSSProperties = {
    marginBottom: '12px'
  };

  const textStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0084ff',
    marginBottom: '4px'
  };

  const subtextStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#666'
  };

  return (
    <div style={overlayStyle}>
      <div style={contentStyle}>
        <div style={iconStyle}>
          <Upload size={40} color="#0084ff" />
        </div>
        <div style={textStyle}>Drop files to upload</div>
        <div style={subtextStyle}>JPG, PNG, MP4, 3GP, PDF supported</div>
      </div>
    </div>
  );
};

// ============================================================================
// FILE COUNT BADGE
// ============================================================================

interface FileCountBadgeProps {
  count: number;
  uploading?: boolean;
}

export const FileCountBadge: React.FC<FileCountBadgeProps> = ({ count, uploading }) => {
  if (count === 0) return null;

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
    background: uploading ? '#e3f2fd' : '#f5f5f5',
    color: uploading ? '#1976d2' : '#666',
    padding: '2px 6px',
    borderRadius: '8px',
    fontSize: '10px',
    fontWeight: '500',
    border: `1px solid ${uploading ? '#bbdefb' : '#e0e0e0'}`,
    marginLeft: '4px'
  };

  return (
    <div style={badgeStyle}>
      <span>{count}</span>
      {uploading && (
        <div style={{
          width: '8px',
          height: '8px',
          border: '1px solid #e0e0e0',
          borderTop: '1px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
      )}
    </div>
  );
};

// ============================================================================
// CSS ANIMATIONS
// ============================================================================

// Inject CSS animations if not present
if (typeof document !== 'undefined') {
  const styleId = 'multi-file-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
}